if (!customElements.get('nestful-sticky-cart')) {
  class NestfulStickyCart extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.info = this.closest('product-info');
      this.form = document.getElementById(this.dataset.form);
      this.submit = this.form?.querySelector('[name="add"]');
      this.button = this.querySelector('button');
      if (!this.info || !this.submit) return;
      this.config = JSON.parse(this.querySelector('[data-sticky-config]').textContent);
      this.confirmed = new Set(this.config.explicit ? this.config.required : []);
      this.events = new AbortController();
      const on = (target, name, fn, extra = {}) => target.addEventListener(name, fn, {signal:this.events.signal, ...extra});
      this.schedule = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.render(); });
      };
      const confirm = (event) => {
        if (!event.isTrusted || !event.target.matches('variant-selects input[type="radio"], variant-selects select')) return;
        if (event.type === 'click' && event.target.tagName === 'SELECT') return;
        const group = event.target.closest('.product-form__input');
        const index = this.groups().indexOf(group);
        if (index >= 0) this.confirmed.add(index + 1);
        this.variantScrollRequested = window.matchMedia('(max-width: 749px)').matches;
        this.queueVariantScroll();
        this.schedule();
      };
      on(this.info, 'click', confirm, {capture:true});
      on(this.info, 'change', confirm, {capture:true});
      on(this.info, 'change', this.schedule);
      on(this.info, 'input', this.schedule);
      // A deliberate submission of the main form also confirms its current selection.
      on(this.form, 'submit', () => {
        this.config.required.forEach(position => this.confirmed.add(position));
        this.schedule();
      });
      on(this.button, 'click', () => this.activate());
      on(window, 'scroll', this.schedule, {passive:true});
      on(window, 'resize', this.schedule, {passive:true});
      on(window, 'pageshow', this.schedule);
      this.observer = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target))) this.schedule();
      });
      this.observer.observe(this.info, {subtree:true, childList:true, characterData:true, attributes:true,
        attributeFilter:['disabled','aria-disabled','class','hidden','data-blocked']});
      this.bodyObserver = new MutationObserver(this.schedule);
      this.bodyObserver.observe(document.body, {attributes:true, attributeFilter:['class']});
      this.resizeObserver = new ResizeObserver(this.schedule);
      this.resizeObserver.observe(this.info);
      this.unsubscribe = typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined'
        ? subscribe(PUB_SUB_EVENTS.cartError, ({productVariantId}) => {
          if (this.fromSticky && String(productVariantId) === this.form.querySelector('[name="id"]').value) {
            this.fromSticky = false;
            this.reveal(this.form.closest('product-form'));
          }
        }) : null;
      this.unsubscribeVariant = typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined'
        ? subscribe(PUB_SUB_EVENTS.variantChange, ({data}) => {
          if (data.sectionId === this.info.dataset.section) this.queueVariantScroll();
        }) : null;
      this.render();
    }
    queueVariantScroll() {
      cancelAnimationFrame(this.variantScrollFrame);
      this.variantScrollFrame = requestAnimationFrame(() => {
        this.variantScrollFrame = 0;
        if (!this.isConnected || !this.variantScrollRequested || this.pendingGroup() ||
            !window.matchMedia('(max-width: 749px)').matches) return;
        let variant;
        try { variant = JSON.parse(this.info.querySelector('variant-selects [data-selected-variant]')?.textContent); }
        catch { return; }
        const values = this.groups().map(group => group.querySelector('input:checked,select')?.value);
        if (!variant?.available || !variant.options?.every((value, index) => value === values[index]) ||
            this.submit.disabled || this.submit.getAttribute('aria-disabled') === 'true') return;
        const gallery = this.info.querySelector('media-gallery');
        const media = gallery?.querySelector('.product__media-item.is-active') || gallery?.querySelector('.product__media-item');
        this.variantScrollRequested = false;
        if (!media) return;
        const offset = Math.max(this.headerSpace(),
          parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 56) + 8;
        const rect = media.getBoundingClientRect();
        const fits = rect.bottom <= window.innerHeight || rect.height > window.innerHeight - offset;
        if (rect.top >= offset - 0.5 && fits) return;
        gallery.setAttribute('tabindex', '-1');
        gallery.focus({preventScroll:true});
        window.scrollTo({top:Math.max(0, window.scrollY + rect.top - offset),
          behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      });
    }
    groups() { return [...(this.info.querySelector('variant-selects')?.querySelectorAll('.product-form__input') || [])]; }
    pendingGroup() {
      const groups = this.groups();
      const position = this.config.required.find(position => !this.confirmed.has(position));
      return position ? groups[position - 1] || this.info.querySelector('variant-selects') : null;
    }
    headerSpace() {
      const header = document.querySelector('.shopify-section-header');
      const rect = header?.getBoundingClientRect();
      return rect && rect.top <= 0 && rect.bottom > 0 ? rect.bottom : 0;
    }
    render() {
      if (!this.isConnected) return;
      const rect = this.submit.getBoundingClientRect();
      const overlay = document.body.classList.contains('overflow-hidden') ||
        document.body.classList.contains('overflow-hidden-mobile') ||
        document.querySelector('cart-drawer.active, modal-dialog[open], dialog[open]');
      this.hidden = !rect.height || rect.bottom > this.headerSpace() || !!overlay;
      const pending = !!this.pendingGroup();
      this.renderSummary(pending);
      const loading = this.submit.classList.contains('loading') || this.submit.getAttribute('aria-disabled') === 'true';
      const disabled = !pending && (this.submit.disabled || loading);
      if (this.button.disabled !== disabled) this.button.disabled = disabled;
      const label = pending ? this.dataset.choose : this.submit.disabled && !loading
        ? this.submit.querySelector('span')?.textContent.trim() || this.dataset.add : this.dataset.add;
      const text = this.button.querySelector('[data-sticky-label]');
      if (text.textContent !== label) text.textContent = label;
      this.button.classList.toggle('loading', !pending && loading);
      this.button.setAttribute('aria-busy', String(!pending && loading));
      this.button.querySelector('.loading__spinner')?.classList.toggle('hidden', pending || !loading);
      this.button.setAttribute('aria-controls', pending ? this.info.querySelector('variant-selects').id : this.form.id);
      if (this.fromSticky && !loading) {
        const error = this.form.closest('product-form').querySelector('.product-form__error-message-wrapper:not([hidden])');
        if (error) { this.fromSticky = false; this.reveal(error); }
      }
    }
    renderSummary(pending) {
      if (!this.querySelector('[data-sticky-product]')) return;
      const setText = (selector, value) => {
        const element = this.querySelector(selector);
        if (element && element.textContent !== value) element.textContent = value;
      };
      const quantity = Number(this.form.elements.namedItem('quantity')?.value ?? 1);
      const validQuantity = Number.isSafeInteger(quantity) && quantity > 0;
      const values = this.groups().map(group => {
        const selected = group.querySelector('input:checked,select');
        return selected?.value;
      }).filter(Boolean);
      const quantityText = validQuantity ? this.dataset.quantity.replace('[quantity]', String(quantity)) : '';
      const selection = [pending ? this.dataset.pending : values.join(' / '), quantityText].filter(Boolean).join(' · ');
      setText('[data-sticky-selection]', selection);
      this.querySelector('[data-sticky-selection]').title = selection;
      const bundle = this.info.querySelector('nestful-bundle');
      const variantJSON = this.info.querySelector('variant-selects [data-selected-variant]');
      let variant = null;
      try { variant = variantJSON ? JSON.parse(variantJSON.textContent) : null; } catch { /* Keep the original price until the next variant render. */ }
      const unitPrice = variant?.price ?? this.config.price;
      const bundlePrice = bundle?.querySelector('.total')?.textContent.trim();
      const busy = this.submit.disabled || this.submit.classList.contains('loading') || bundle?.dataset.blocked === 'true';
      const amount = validQuantity && !busy
        ? bundlePrice || new Intl.NumberFormat(this.config.locale, {style:'currency', currency:this.config.currency}).format(unitPrice * quantity / 100)
        : '—';
      setText('[data-sticky-amount]', amount);
      setText('[data-sticky-price-label]', quantity > 1 ? this.dataset.total : this.dataset.unit);
      const image = this.querySelector('.nf-sticky-image');
      const source = variant?.featured_image?.src;
      if (image && source && image.dataset.variantSrc !== source) {
        image.dataset.variantSrc = source;
        image.removeAttribute('srcset');
        image.src = source;
      }
    }
    activate() {
      const pending = this.pendingGroup();
      if (pending) { this.reveal(pending); return; }
      // Reuse the original form: quantity, bundle validation and cart drawer stay in sync.
      if (this.submit.disabled || this.submit.getAttribute('aria-disabled') === 'true') return;
      const invalid = [...this.form.elements].find(input => input.willValidate && !input.validity.valid);
      if (invalid) { this.reveal(invalid); invalid.reportValidity(); return; }
      this.fromSticky = true;
      this.form.requestSubmit(this.submit);
      this.render();
    }
    reveal(target) {
      const rootStyles = getComputedStyle(document.documentElement);
      const offset = Math.max(this.headerSpace(), parseFloat(rootStyles.getPropertyValue('--header-height')) || 56) + 16;
      const focus = target.matches('input,select,button') ? target : target.querySelector('input:checked,select,input:not([type="hidden"]),button:not([disabled])') || target;
      if (!focus.matches('input,select,button,a,[tabindex]')) focus.setAttribute('tabindex','-1');
      focus.focus({preventScroll:true});
      window.scrollTo({top:Math.max(0, window.scrollY + target.getBoundingClientRect().top - offset),
        behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
    }
    disconnectedCallback() {
      this.events?.abort(); this.events = null;
      this.observer?.disconnect(); this.bodyObserver?.disconnect(); this.resizeObserver?.disconnect();
      this.unsubscribe?.(); this.unsubscribeVariant?.();
      cancelAnimationFrame(this.variantScrollFrame); this.variantScrollFrame = 0;
      cancelAnimationFrame(this.frame); this.frame = 0;
    }
  }
  customElements.define('nestful-sticky-cart', NestfulStickyCart);
}
